'use client';
import React, { useState } from 'react';
import { Search, ChevronLeft, ChevronRight, Eye, Ban, Mail } from 'lucide-react';
import { toast } from 'sonner';

const mockUsers = [
  { id: 'user-001', name: 'Amara Osei', email: 'amara.osei@gmail.com', country: 'Ghana', blocksOwned: 3, totalSpent: '$15.00', joined: 'May 17, 2026', status: 'active', lastCategory: 'finances' },
  { id: 'user-002', name: 'Léa Fontaine', email: 'lea.f@outlook.fr', country: 'France', blocksOwned: 1, totalSpent: '$5.00', joined: 'May 17, 2026', status: 'active', lastCategory: 'love' },
  { id: 'user-003', name: 'Rajan Mehta', email: 'rajan.mehta@proton.me', country: 'India', blocksOwned: 7, totalSpent: '$35.00', joined: 'Apr 12, 2026', status: 'active', lastCategory: 'health' },
  { id: 'user-004', name: 'Sofia Reyes', email: 'sofia.r@icloud.com', country: 'Mexico', blocksOwned: 2, totalSpent: '$10.00', joined: 'May 14, 2026', status: 'active', lastCategory: 'peace' },
  { id: 'user-005', name: 'Kofi Asante', email: 'k.asante@gmail.com', country: 'Ghana', blocksOwned: 12, totalSpent: '$60.00', joined: 'Mar 22, 2026', status: 'active', lastCategory: 'career' },
  { id: 'user-006', name: 'Yuki Tanaka', email: 'yuki.t@yahoo.co.jp', country: 'Japan', blocksOwned: 5, totalSpent: '$25.00', joined: 'Apr 30, 2026', status: 'active', lastCategory: 'family' },
  { id: 'user-007', name: 'Dmitri Volkov', email: 'dmitri.v@mail.ru', country: 'Russia', blocksOwned: 0, totalSpent: '$0.00', joined: 'May 16, 2026', status: 'suspended', lastCategory: '—' },
  { id: 'user-008', name: 'Maria Santos', email: 'maria.santos@gmail.com', country: 'Brazil', blocksOwned: 4, totalSpent: '$20.00', joined: 'May 01, 2026', status: 'active', lastCategory: 'love' },
  { id: 'user-009', name: 'Ibrahim Al-Farsi', email: 'ibrahim.af@gmail.com', country: 'UAE', blocksOwned: 9, totalSpent: '$45.00', joined: 'Feb 14, 2026', status: 'active', lastCategory: 'health' },
  { id: 'user-010', name: 'Chioma Eze', email: 'chioma.eze@hotmail.com', country: 'Nigeria', blocksOwned: 6, totalSpent: '$30.00', joined: 'Apr 05, 2026', status: 'active', lastCategory: 'finances' },
];

export default function AdminUsersTable() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 8;

  const filtered = mockUsers?.filter(
    (u) =>
      u?.name?.toLowerCase()?.includes(search?.toLowerCase()) ||
      u?.email?.toLowerCase()?.includes(search?.toLowerCase()) ||
      u?.country?.toLowerCase()?.includes(search?.toLowerCase())
  );
  const paginated = filtered?.slice((page - 1) * perPage, page * perPage);
  const totalPages = Math.ceil(filtered?.length / perPage);

  return (
    <div className="cosmic-card rounded-2xl border border-border overflow-hidden">
      <div className="flex items-center justify-between p-5 border-b border-border">
        <div>
          <h3 className="font-display font-semibold text-foreground">Registered Seekers</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{filtered?.length} users · 12,483 total registered</p>
        </div>
        <div className="relative w-64">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e?.target?.value); setPage(1); }}
            placeholder="Search users…"
            className="w-full pl-8 pr-3 py-2 text-sm rounded-lg bg-input border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50"
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              {['Name', 'Email', 'Country', 'Blocks Owned', 'Total Spent', 'Joined', 'Last Category', 'Status', 'Actions']?.map((col) => (
                <th key={`ucol-${col}`} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginated?.map((user, i) => (
              <tr key={user?.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors group"
                style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                <td className="px-4 py-3 font-medium text-foreground whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-accent/20 border border-accent/30 flex items-center justify-center text-xs font-semibold text-accent flex-shrink-0">
                      {user?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                    </div>
                    {user?.name}
                  </div>
                </td>
                <td className="px-4 py-3 text-muted-foreground text-xs">{user?.email}</td>
                <td className="px-4 py-3 text-muted-foreground text-xs">{user?.country}</td>
                <td className="px-4 py-3 text-foreground font-semibold metric-number text-center">{user?.blocksOwned}</td>
                <td className="px-4 py-3 text-primary font-semibold metric-number">{user?.totalSpent}</td>
                <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap">{user?.joined}</td>
                <td className="px-4 py-3 capitalize text-xs text-muted-foreground">{user?.lastCategory}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    user?.status === 'active' ? 'text-success bg-success/10' : 'text-danger bg-danger/10'
                  }`}>
                    {user?.status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button title="View user profile" className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-all">
                      <Eye size={14} />
                    </button>
                    <button title="Send email to user" className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-all">
                      <Mail size={14} />
                    </button>
                    <button
                      title="Suspend user account"
                      onClick={() => toast?.error(`User ${user?.name} suspension requires confirmation`)}
                      className="p-1.5 rounded-lg hover:bg-danger/10 text-muted-foreground hover:text-danger transition-all"
                    >
                      <Ban size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between px-5 py-3 border-t border-border">
        <p className="text-xs text-muted-foreground">Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, filtered?.length)} of {filtered?.length}</p>
        <div className="flex items-center gap-1">
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground disabled:opacity-40">
            <ChevronLeft size={15} />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)?.map((p) => (
            <button key={`usr-page-${p}`} onClick={() => setPage(p)}
              className={`w-7 h-7 rounded-lg text-xs font-medium transition-all ${page === p ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:bg-muted'}`}>
              {p}
            </button>
          ))}
          <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}
            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground disabled:opacity-40">
            <ChevronRight size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}